import { defineStore } from "pinia";
import { useSpaceStore } from "./spaceStore";
import { useTreeStore } from "./treeStore";
import { inject, reactive, Ref, ref, toRaw } from "vue";

import valueRequiredFromLevel1 from "../data/generated/valueRequiredFromLevel1.json";
import valueRequiredFromLevel2 from "../data/generated/valueRequiredFromLevel2.json";
import valueRequiredFromLevel3 from "../data/generated/valueRequiredFromLevel3.json";

import fieldByPart from "../data/generated/fieldByPart.json";

import {
  groupByElement,
  mergeCalculationQueue,
  splitRequiredFromField
} from "../utils/calculateHelper";
import { Nullable } from "vitest";
import { useMainStore } from "./mainStore";
import { useSocketStore } from "./socketStore";

export const useCalculationStore = defineStore("calculationStore", () => {
  const spaceStore = useSpaceStore();
  const treeStore = useTreeStore();
  const mainStore = useMainStore();
  const socketStore = useSocketStore();
  const rustWorker: Worker = inject("rustWorker");

  let rustWorkerReady: boolean = false;

  function getCypressName(id: string): string {
    return spaceStore.calculationElements[id].name;
  }

  function getValueRequiredFrom(
    element_id: string,
    field_name: string
  ): string[] {
    const level = spaceStore.calculationElements[element_id].level;
    if (level === 3) {
      return valueRequiredFromLevel3[field_name] || [];
    } else if (level === 2) {
      return valueRequiredFromLevel2[field_name] || [];
    } else {
      return valueRequiredFromLevel1[field_name] || [];
    }
  }

  function getValueRequiredFromByLevel(
    element_id: string,
    field_name: string
  ): Record<string, number> {
    const result = {};
    for (const field of valueRequiredFromLevel1[field_name] || []) {
      if (!result.hasOwnProperty(field)) {
        result[field] = 0;
      }
      result[field] |= 1;
    }

    for (const field of valueRequiredFromLevel2[field_name] || []) {
      if (!result.hasOwnProperty(field)) {
        result[field] = 0;
      }
      result[field] |= 2;
    }

    for (const field of valueRequiredFromLevel3[field_name] || []) {
      if (!result.hasOwnProperty(field)) {
        result[field] = 0;
      }
      result[field] |= 4;
    }

    return result;
  }

  rustWorker.onmessage = (messageEvent) => {
    if (messageEvent.data === "workerReady") {
      rustWorkerReady = true;
      if (calculating.value) {
        calculateNext();
      }
      console.log("[INFO]", "[OnMessage]", messageEvent.data);
      if (Object.keys(spaceStore.calculationElements).length > 0) {
        rustWorker.postMessage({
          task: "init",
          data: { data: toRaw(spaceStore.calculationElements) }
        });
      }
      return;
    }

    if (messageEvent.data) {
      const onlyCurrent = !!messageEvent.data.current;

      let messageData: { id: string; field: string; value: string }[];
      if (onlyCurrent) {
        messageData = messageEvent.data.result;
      } else {
        // Array of updated values
        messageData = messageEvent.data;
      }
      console.log(
        "[INFO]",
        "[OnMessage]",
        messageData.reduce((result, entry, index) => {
          if (index === 0) {
            result += "Calculation Results:\n" + entry.id + ":\n";
          }
          result += "\t" + entry.field + ":\t" + entry.value + "\n";

          return result;
        }, "")
      );

      const changedElements: string[] = [];

      for (const element of messageData) {
        if (
          !spaceStore.calculationElements.hasOwnProperty(element.id) ||
          !spaceStore.calculationElements[element.id].hasOwnProperty(
            element.field
          )
        ) {
          console.warn(
            "Something went wrong, missing element or field",
            element
          );
          continue;
        }

        const updateServer = toUpload.delete(element.id + "|" + element.field);

        // if (
        //   spaceStore.calculationElements[element.id][element.field] ===
        //   element.value
        // ) {
        //   continue;
        // }

        spaceStore.calculationElements[element.id][element.field] =
          element.value;

        if (updateServer) {
          changedElements.push(element.id + "|" + element.field);
        }
      }

      const changedElementsGrouped = groupByElement(changedElements);

      for (const [element_id, fields] of Object.entries(
        changedElementsGrouped
      )) {
        if (!spaceStore.calculationElements[element_id].temporary) {
          spaceStore.updateElement(element_id, fields);
        }
      }

      if (!stepByStepCalculation.value) {
        // !onlyCurrent &&
        calculateNext();
      }
    }
  };

  const buildingCalculationQueue = ref(false);

  const focusCalculationSocket: Record<string, calculationQueue> = reactive({});

  const toUpload = reactive(new Set<string>());

  const focusCalculation = reactive<calculationQueue>({
    toUpload: [],
    outputQuantityQueue: [],
    normalElements: {},
    normalQueue: [],
    extraElements: {}
  });

  const calculationQueue = reactive<calculationQueue>({
    outputQuantityQueue: [],
    normalElements: {},
    normalQueue: [],
    extraElements: {}
  });

  const stepByStepCalculation = ref(false);
  const currentCalculation: Ref<
    Nullable<{ type: string; elements: Record<string, undefined | string[]> }>
  > = ref(null);

  const freezeCalculation = ref(0);

  const calculating = ref(false);

  function removeElementCalculation(
    element_id: string,
    fromSocket: boolean = false
  ) {
    console.log(
      "[createRemoveQueue]",
      "[DEBUG]",
      "remove an element",
      element_id
    );

    const { normalQueue, downQueue, extraQueue } =
      buildMultiCalculationQueue(element_id);

    !fromSocket &&
      [...normalQueue, ...downQueue, ...extraQueue].forEach((i) =>
        toUpload.add(i)
      );

    calculationQueue.outputQuantityQueue.push(...downQueue);

    calculationQueue.normalElements = mergeCalculationElements(
      calculationQueue.normalElements,
      sortAndGroupByElement(normalQueue)
    );
    calculationQueue.normalQueue = mergeCalculationQueue(
      calculationQueue.normalQueue,
      orderCalculationElements([
        ...downQueue.map((element) => element.split("|")[0]).reverse(),
        element_id
      ])
    );

    calculationQueue.extraElements = mergeCalculationElements(
      calculationQueue.extraElements,
      sortAndGroupByElement(extraQueue)
    );

    // remove element itself because it does not longer exists
    calculationQueue.outputQuantityQueue =
      calculationQueue.outputQuantityQueue.filter(
        (element) => element !== element_id
      );

    delete calculationQueue.normalElements[element_id];
    calculationQueue.normalQueue = calculationQueue.normalQueue.filter(
      (element) => element !== element_id
    );

    delete calculationQueue.extraElements[element_id];
  }

  function addElementCalculation(
    elementId: string,
    relevantElements: string[] = [],
    fromSocket: boolean = false
  ) {
    console.log("[createAddQueue]", "[DEBUG]", "add an element", elementId);

    const { normalQueue, downQueue, extraQueue } =
      buildMultiCalculationQueue(elementId);

    !fromSocket &&
      [...normalQueue, ...downQueue, ...extraQueue].forEach((i) =>
        toUpload.add(i)
      );

    calculationQueue.outputQuantityQueue.push(...downQueue);

    calculationQueue.normalElements = mergeCalculationElements(
      calculationQueue.normalElements,
      sortAndGroupByElement(normalQueue, relevantElements)
    );
    calculationQueue.normalQueue = mergeCalculationQueue(
      calculationQueue.normalQueue,
      orderCalculationElements([
        ...downQueue.map((element) => element.split("|")[0]).reverse(),
        elementId
      ])
    );

    calculationQueue.extraElements = mergeCalculationElements(
      calculationQueue.extraElements,
      sortAndGroupByElement(extraQueue)
    );
  }

  function buildCalculationQueue(
    element_id: string,
    field_name: string,
    queue: buildingQueue = { normalQueue: [], downQueue: [], extraQueue: [] }
  ): buildingQueue {
    // get all fields that are dependent on this field

    const required_from = getValueRequiredFromByLevel(element_id, field_name);
    // stop this branch
    if (Object.keys(required_from).length === 0) {
      return queue;
    }

    // console.log(required_from, getValueRequiredFrom(element_id, field_name));

    for (const [required_from_field, levels] of Object.entries(required_from)) {
      const { required_type, field } =
        splitRequiredFromField(required_from_field);

      // get element type from the field name (e.g. "part", "labor", ...)
      const field_element_type = field.split("_").at(-1);
      if (!field_element_type) {
        console.warn(
          "Something went wront",
          field_name,
          element_id,
          required_type,
          field
        );
      }

      let id = element_id;

      if (required_type === "C" || required_type === "I") {
        // Since the formulas are getting flipped to determine who requires something, a C element is required from a parent
        if (required_type === "C") {
          id = spaceStore.calculationElements[id].parent;
          if (!id) {
            continue;
          }

          if (
            element_id.endsWith("part") &&
            spaceStore.calculationElements[element_id].is_active_part === 0
          ) {
            continue;
          }
        }

        const queueElement = id + "|" + field;
        if (!spaceStore.includesLevel(id, levels)) {
          // not relevant for this level
          continue;
        }

        if (queue.normalQueue.indexOf(queueElement) !== -1) {
          // element is already in queue -> stop
          continue;
        }

        queue.normalQueue.push(queueElement);
        buildCalculationQueue(id, field, queue);
      } else if (required_type === "L") {
        const location_id = element_id.endsWith("location")
          ? element_id
          : spaceStore.calculationElements[element_id].location;

        // list of all elements that use current location
        const affectedElements =
          treeStore.locationDependencies[location_id] || [];

        for (const affectedElement of affectedElements) {
          // only push elements to the queue with the correct type
          if (affectedElement.endsWith(field_element_type)) {
            if (!spaceStore.includesLevel(affectedElement, levels)) {
              // not relevant for this level
              continue;
            }
            queue.normalQueue.push(affectedElement + "|" + field);
            buildCalculationQueue(affectedElement, field, queue);
          }
        }
      } else if (required_type === "D" || required_type === "Y") {
        if (required_type === "Y") {
          // Internal Value
          //

          if (!spaceStore.includesLevel(id, levels)) {
            // not relevant for this level
            continue;
          }

          if (queue.downQueue.indexOf(id + "|" + field) === -1) {
            queue.downQueue.push(id + "|" + field);
            buildCalculationQueue(id, field, queue);
          }
        } else {
          // calculated down the tree for output qunatities
          const affectedElements = Object.entries(
            spaceStore.calculationElements
          )
            .filter(
              ([key, element]) =>
                key.endsWith(field_element_type) &&
                element.parent === element_id
            )
            .reduce((result, [key, _]) => [...result, key], []);

          for (const affectedElement of affectedElements) {
            if (!spaceStore.includesLevel(affectedElement, levels)) {
              // not relevant for this level
              continue;
            }

            if (queue.downQueue.indexOf(affectedElement + "|" + field) === -1) {
              queue.downQueue.push(affectedElement + "|" + field);
              buildCalculationQueue(affectedElement, field, queue);
            }
          }
        }
      } else if (
        required_type === "E" ||
        required_type === "P" ||
        required_type === "Z"
      ) {
        if (required_type === "Z") {
          // Internal Value
          //
          if (!spaceStore.includesLevel(id, levels)) {
            // not relevant for this level
            continue;
          }
          queue.extraQueue.push(id + "|" + field);
          buildCalculationQueue(id, field, queue);
        } else {
          // get all elements that are calculated down, for final values
          const affectedElements = Object.entries(
            spaceStore.calculationElements
          )
            .filter(
              ([key, element]) =>
                key.endsWith(field_element_type) &&
                (required_type === "E" ? element.parent : element.location) ===
                  element_id
            )
            .reduce((result, [key, _]) => [...result, key], []);

          for (const affectedElement of affectedElements) {
            if (!spaceStore.includesLevel(affectedElement, levels)) {
              // not relevant for this level
              continue;
            }
            queue.extraQueue.push(affectedElement + "|" + field);
            buildCalculationQueue(affectedElement, field, queue);
          }
        }
      } else {
        console.error(
          "[ERROR]",
          "[BuildCalculation]",
          "Should never happen: ",
          required_type + " - " + field
        );
      }
    }

    return queue;
  }

  function focusElement(id: string, field: string) {
    console.log("[FocusElement]", "[DEBUG]", "focus field", id, field);

    if (freezeCalculation.value === 2) {
      // calculation is already frozen
      removeFreeze();
      freezeCalculation.value = 1;
    }

    if (Object.keys(focusCalculation.normalElements).length) {
      // Should not happen. Build handle later
      console.error(
        "Currently having an other focus queue. Should never happen",
        id,
        field
      );
      return;
    }

    console.time("focusElement");
    const { normalQueue, downQueue, extraQueue } = buildCalculationQueue(
      id,
      field
    );

    focusCalculation.toUpload = [...normalQueue, ...downQueue, ...extraQueue];

    focusCalculation.outputQuantityQueue = downQueue;
    focusCalculation.normalElements = sortAndGroupByElement(normalQueue);

    focusCalculation.normalQueue = orderCalculationElements([
      ...downQueue.map((element) => element.split("|")[0]).reverse(),
      id
    ]);
    focusCalculation.extraElements = sortAndGroupByElement(extraQueue);

    console.timeEnd("focusElement");

    if (freezeCalculation.value === 1) {
      freezeCalculation.value = 2;
    }

    return focusCalculation;
  }

  function buildMultiCalculationQueue(element_id: string) {
    const queue = { normalQueue: [], downQueue: [], extraQueue: [] };
    for (const field of Object.keys(
      spaceStore.calculationElements[element_id]
    )) {
      buildCalculationQueue(element_id, field, queue);
    }

    return queue;
  }

  function orderCalculationElements(starting_elements: string[]) {
    const result: string[] = [];
    let element: string | undefined;

    // go over every starting element
    while ((element = starting_elements.shift())) {
      /** use an array to garanty correct order \
       * use a temporay interim result to splice it later in the correct slot
       */
      const subResultList: string[] = [];

      while (element) {
        subResultList.push(element);
        // remove element from starting elements, if exists
        starting_elements = starting_elements.filter(
          (starting_element) => element !== starting_element
        );

        let parent = spaceStore.calculationElements[element].parent;

        // Final Element, Push to queue
        if (!parent) {
          result.push(...subResultList);

          // if location, start with every element that depends on that location
          if (
            element.endsWith("location") &&
            treeStore.locationDependencies[element]
          ) {
            starting_elements.push(...treeStore.locationDependencies[element]);
          }
          break;
        }

        // find index of parent in result array
        const parentIndexResultList = result.indexOf(parent);

        // if parent is in result array, break the loop and splice interim result in the slot before the parent
        if (parentIndexResultList !== -1) {
          result.splice(parentIndexResultList, 0, ...subResultList);
          break;
        }

        element = parent;
      }
    }

    return result;
  }

  function calculateOnlyCurrent(
    element_id: string,
    field: string,
    value: string
  ) {
    if (freezeCalculation.value) {
      return;
    }

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field,
        value: typeof value === "boolean" ? Number(value).toString() : value
      }
    });
    rustWorker.postMessage({
      task: "calculateCurrent",
      data: {
        id: element_id,
        fields: Object.values(
          Object.assign(
            {},
            toRaw(focusCalculation.normalElements[element_id]),
            toRaw(focusCalculation.extraElements[element_id])
          )
        )
      }
    });
  }

  function executeFocusCalculation() {
    calculationQueue.outputQuantityQueue.push(
      ...focusCalculation.outputQuantityQueue
    );

    calculationQueue.normalElements = mergeCalculationElements(
      calculationQueue.normalElements,
      focusCalculation.normalElements
    );
    calculationQueue.normalQueue = mergeCalculationQueue(
      calculationQueue.normalQueue,
      focusCalculation.normalQueue
    );

    calculationQueue.extraElements = mergeCalculationElements(
      calculationQueue.extraElements,
      focusCalculation.extraElements
    );

    console.log("toUpload", toRaw(focusCalculation.toUpload));
    focusCalculation.toUpload.forEach((i) => toUpload.add(i));

    startCalculating();
  }

  function preChangeValue(element_id: string, field: string, value: string) {
    switch (field) {
      default:
      // do nothing
    }
  }

  function clearFocusQueue() {
    // remove focus calculation
    focusCalculation.outputQuantityQueue = [];
    focusCalculation.normalElements = {};
    focusCalculation.normalQueue = [];
    focusCalculation.extraElements = {};
    focusCalculation.toUpload = [];
  }

  function postChangeValue(element_id: string, field: string, value: string) {
    switch (field) {
      case "name":
      case "variant_name":
      case "manufacturing_step_name":
        treeStore.changeInHierachy(element_id);
        break;
      case "location":
        const realLocation = value.endsWith("_part")
          ? treeStore.elementLocation[value]
          : value;
        const shiftModel =
          spaceStore
            .getElementList(realLocation, "shiftModel")
            .filter((element) => element.active_shiftModel === "1")
            ?.at(0)?.fullId ?? "";

        addElementCalculation(realLocation);

        const [changed, missing] = treeStore.changeRealLocations(element_id);
        spaceStore.updateMissingInputs(missing);
        addElementCalculation(realLocation, changed);
        focusElement(element_id, "shift_model_part");
        changeValue(element_id, "shift_model_part", shiftModel);
      default:
      // do nothing
    }
  }

  function changeNormalValue(element_id: string, field: string, value: string) {
    preChangeValue(element_id, field, value);
    spaceStore.calculationElements[element_id][field] = value;
    spaceStore.updateElement(element_id, [field]);
    postChangeValue(element_id, field, value);
  }

  function changeValue(element_id: string, field: string, value: string) {
    console.log(
      "[ChangeValue]",
      "[DEBUG]",
      "change value",
      element_id,
      field,
      value
    );

    if (freezeCalculation.value) {
      return;
    }

    spaceStore.removeMissingInput(element_id, field);

    if (value === spaceStore.calculationElements[element_id][field]) {
      // Value didnt change, so dont calculate
      // TODO when live, remove this. For now is relevant to control everything calculates correctly
      // calculateOnlyCurrent(element_id, field, value);
      // return;
    }

    preChangeValue(element_id, field, value);

    spaceStore.calculationElements[element_id][field] = value;

    spaceStore.updateElement(element_id, [field], true);

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field,
        value: typeof value === "boolean" ? Number(value).toString() : value
      }
    });

    executeFocusCalculation();

    postChangeValue(element_id, field, value);
  }

  function changeLevel(element_id: string, level: number) {
    if (
      element_id.endsWith("_machine") &&
      spaceStore.calculationElements[element_id].level === 1 &&
      level === 2
    ) {
      spaceStore.calculationElements[element_id].comment +=
        "\n" +
        new Date().toLocaleString("de-DE") +
        ": Level 1 -> 2: Alter Wert: " +
        spaceStore.calculationElements[element_id].machine_hourly_rate_machine +
        " €/h";
    }

    spaceStore.calculationElements[element_id].level = level;
    spaceStore.updateElement(element_id, ["level"]);

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field: "level",
        value: level
      }
    });

    addElementCalculation(element_id);
    startCalculating();
    socketStore.socket.emit("change-level", element_id, level);
  }

  function changeActiveVariant(oldActiveId: string, newActiveId: string) {
    spaceStore.calculationElements[oldActiveId].is_active_part = 0;
    spaceStore.calculationElements[newActiveId].is_active_part = 1;

    rustWorker.postMessage({
      task: "change",
      data: {
        id: oldActiveId,
        field: "is_active_part",
        value: 0
      }
    });

    rustWorker.postMessage({
      task: "change",
      data: {
        id: newActiveId,
        field: "is_active_part",
        value: 1
      }
    });

    spaceStore.updateElement(oldActiveId, ["is_active_part"]);
    spaceStore.updateElement(newActiveId, ["is_active_part"]);

    addElementCalculation(newActiveId);
    startCalculating();
  }

  function startCalculating() {
    if (calculating.value) {
      // dont start a next calculation queue
    } else {
      calculating.value = true;
      if (!stepByStepCalculation.value && rustWorkerReady) {
        calculateNext();
      }
    }
  }

  function calculateNext() {
    console.log("CalculateNext");
    if (calculationQueue.outputQuantityQueue.length) {
      console.log("calculate output quantity");

      rustWorker.postMessage({
        task: "calculate_output_quantities",
        data: {
          fields: toRaw(calculationQueue.outputQuantityQueue)
        }
      });
      currentCalculation.value = {
        type: "outputQuantities",
        elements: toRaw(calculationQueue.outputQuantityQueue).reduce(
          (result, entry) => {
            const [id, field] = entry.split("|");
            result[id] = [field];
            return result;
          },
          {}
        )
      };
      calculationQueue.outputQuantityQueue.length = 0;
    } else if (calculationQueue.normalQueue.length) {
      let id = calculationQueue.normalQueue.shift();

      console.log("calculate next normal element: " + id);

      rustWorker.postMessage({
        task: "calculate",
        data: {
          id,
          fields: toRaw(calculationQueue.normalElements[id])
        }
      });
      currentCalculation.value = {
        type: "normal",
        elements: {
          [id]: toRaw(calculationQueue.normalElements[id])
        }
      };
      delete calculationQueue.normalElements[id];
    } else if (Object.keys(calculationQueue.extraElements).length) {
      let id = Object.keys(calculationQueue.extraElements)[0];

      console.log("calculate extra element: " + id);

      rustWorker.postMessage({
        task: "calculate",
        data: {
          id,
          fields: toRaw(calculationQueue.extraElements[id])
        }
      });
      currentCalculation.value = {
        type: "extra",
        elements: {
          [id]: toRaw(calculationQueue.extraElements[id])
        }
      };
      delete calculationQueue.extraElements[id];
    } else {
      // no more elements to calculate
      calculating.value = false;
      currentCalculation.value = null;
    }

    console.warn("CurrentCalculation", toRaw(currentCalculation.value));
  }

  function removeFreeze() {
    // remove focus calculation
    focusCalculation.outputQuantityQueue = [];
    focusCalculation.normalElements = {};
    focusCalculation.normalQueue = [];
    focusCalculation.extraElements = {};

    freezeCalculation.value = 0;
  }

  function mergeCalculationElements(
    existing: Record<string, string[]>,
    focus: Record<string, string[]>
  ) {
    const result = {};

    for (const [element_id, fields] of Object.entries(focus)) {
      existing[element_id] = [...new Set(fields.concat(existing[element_id]))];
      existing[element_id] = sortByCalculationOrder(
        element_id,
        existing[element_id]
      );
    }

    return existing;
  }

  function sortAndGroupByElement(
    queue: string[],
    relevantElements: string[] = []
  ): Record<string, string[]> {
    const byElement = groupByElement(queue);
    for (const [element_id, fields] of Object.entries(byElement)) {
      if (!relevantElements.includes(element_id)) {
        continue;
      }
      byElement[element_id] = sortByCalculationOrder(element_id, fields);
    }

    return byElement;
  }

  function sortByCalculationOrder(
    element_id: string,
    fields: string[]
  ): string[] {
    const level = spaceStore.calculationElements[element_id].level || 1;
    const [_, type] = element_id.split("_");
    const fieldOrder: string[] =
      fieldByPart[type]?.["calculationOrderLevel" + level] ?? [];
    const result = fieldOrder.filter((field) => fields.includes(field));

    if (fields.length !== result.length) {
      console.warn(
        "Elements gone missing by sorting",
        fields.filter((v) => !result.includes(v))
      );
    }

    return result;
  }

  // socket Calculation
  function focusElementSocket(id: string, field: string, userId: string) {
    console.log("[FocusElement]", "[DEBUG]", "focus field", id, field);

    if (focusCalculationSocket[userId]) {
      // Should not happen. Build handle later
      console.error(
        "Currently having an other socket focus queue. Should never happen",
        id,
        field,
        userId
      );
      return;
    }

    const calculation = {
      outputQuantityQueue: [],
      normalElements: {},
      normalQueue: [],
      extraElements: {}
    };

    const { normalQueue, downQueue, extraQueue } = buildCalculationQueue(
      id,
      field
    );

    calculation.outputQuantityQueue = downQueue;
    calculation.normalElements = sortAndGroupByElement(normalQueue);

    calculation.normalQueue = orderCalculationElements([
      ...downQueue.map((element) => element.split("|")[0]).reverse(),
      id
    ]);
    calculation.extraElements = sortAndGroupByElement(extraQueue);

    focusCalculationSocket[userId] = calculation;
  }

  function calculateOnlyCurrentSocket(
    element_id: string,
    field: string,
    value: string,
    userId: string
  ) {
    if (freezeCalculation.value || !focusCalculationSocket[userId]) {
      return;
    }

    rustWorker.postMessage({
      task: "change",
      data: {
        id: element_id,
        field,
        value: typeof value === "boolean" ? Number(value).toString() : value
      }
    });
    rustWorker.postMessage({
      task: "calculateCurrent",
      data: {
        id: element_id,
        fields: Object.values(
          Object.assign(
            {},
            toRaw(focusCalculationSocket[userId].normalElements[element_id]),
            toRaw(focusCalculationSocket[userId].extraElements[element_id])
          )
        )
      }
    });
  }

  function executeFocusCalculationSocket(userId: string) {
    if (!focusCalculationSocket[userId]) {
      return;
    }

    calculationQueue.outputQuantityQueue.push(
      ...focusCalculationSocket[userId].outputQuantityQueue
    );

    calculationQueue.normalElements = mergeCalculationElements(
      calculationQueue.normalElements,
      focusCalculationSocket[userId].normalElements
    );
    calculationQueue.normalQueue = mergeCalculationQueue(
      calculationQueue.normalQueue,
      focusCalculationSocket[userId].normalQueue
    );

    calculationQueue.extraElements = mergeCalculationElements(
      calculationQueue.extraElements,
      focusCalculationSocket[userId].extraElements
    );
    // remove focus calculation
    delete focusCalculationSocket[userId];

    startCalculating();
  }

  return {
    focusCalculation,
    calculationQueue,
    calculating,
    buildingCalculationQueue,
    focusElement,
    calculateOnlyCurrent,
    changeValue,
    changeNormalValue,
    addElementCalculation,
    removeElementCalculation,
    startCalculating,
    removeFreeze,
    freezeCalculation,
    currentCalculation,
    calculateNext,
    stepByStepCalculation,
    changeLevel,
    changeActiveVariant,
    focusElementSocket,
    calculateOnlyCurrentSocket,
    toUpload,
    executeFocusCalculationSocket,
    focusCalculationSocket,
    clearFocusQueue
  };
});
